import { forwardRef } from 'react';
import { Trans } from 'react-i18next';
import { css } from '@emotion/react';

import { Button, CloseAction, Icon, Text, useTippy, tokens } from '@hotjar/hotsauce';
import { TrafficCoverageLevel } from '@hotjar/webapp/domains/revenue/sampling/types';
import { getHeatmapIndicatorState } from '@hotjar/webapp/domains/revenue/sampling/utils';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { TrafficGaugeStatus } from '@hotjar/webapp/features/SamplingWidget/SessionTrafficCoverage/TrafficGaugeStatus';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { Site } from '@hotjar/site';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';
import { TrafficIndicatorStyles as S } from './styles';

type IndicatorContainerProps = {
  iconComponent: React.ReactElement;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  text: string;
};

type Props = {
  coverageLevel: TrafficCoverageLevel;
  text: string;
  coveragePercentage: number;
  isCoverageMeasureComplete: boolean;
  trackIndicatorClick?: (elementName: string) => void;
};

const UI_POSITION = 'heatmaps_traffic_indicator';

const IndicatorContainer = forwardRef<HTMLButtonElement, IndicatorContainerProps>(
  ({ iconComponent, text, ...otherProps }: IndicatorContainerProps, ref): JSX.Element => (
    <S.StyledIndicatorContainer ref={ref} {...otherProps}>
      {iconComponent}
      <Text>{text}</Text>
    </S.StyledIndicatorContainer>
  ),
);

/**
 * Component that displays the traffic gauge status icon
 */
const IndicatorIcon = ({
  coverageLevel,
  coveragePercentage,
  isCoverageMeasureComplete,
}: Omit<Props, 'text'>): JSX.Element => {
  const showCheckIcon = isCoverageMeasureComplete && coverageLevel === TrafficCoverageLevel.Max;

  if (showCheckIcon) {
    return <Icon size="m" icon="check_alt" color="text_success" />;
  }

  return (
    <TrafficGaugeStatus
      coveragePercentage={coveragePercentage}
      isCoverageMeasureComplete={isCoverageMeasureComplete}
      isHighlighted={false}
    />
  );
};

/**
 * Component that displays the traffic gauge status icon and  text
 */
const HeatmapsIndicatorTrafficStatus = forwardRef<HTMLButtonElement, Props>(
  (
    {
      coveragePercentage,
      text,
      isCoverageMeasureComplete,
      coverageLevel,
      trackIndicatorClick,
    }: Props,
    ref,
  ): JSX.Element => {
    // Only show the check icon if coverage is max and coverage is complete
    // i.e ( > 1 day sessions captured & no filters applied)

    return (
      <IndicatorContainer
        ref={ref}
        iconComponent={
          <IndicatorIcon
            coverageLevel={coverageLevel}
            coveragePercentage={coveragePercentage}
            isCoverageMeasureComplete={isCoverageMeasureComplete}
            trackIndicatorClick={trackIndicatorClick}
          />
        }
        text={text}
        onClick={() => trackIndicatorClick?.('heatmap.indicator-trigger')}
      />
    );
  },
);

/**
 * Adds a <Dropdown /> component that displays session capture data
 * and states, directly in the context of the
 * heatmap page.
 *
 */
export const TrafficIndicatorDropDown = ({
  filtersApplied,
  site,
}: {
  filtersApplied: boolean;
  site: Site | undefined;
}): JSX.Element | null => {
  const { t } = useTranslationWithPrefix('common:heatmap-indicator');
  const tippyInstance = useTippy();

  const { coveragePercentage, days, coverageLevel, isError, isLoading } = useTrafficCoverage(site);
  const { click, viewElement, getGroupDataForSite } = useTrack();

  // We show coverage as complete if you have more than 1 day session capture
  // and have not applied filters
  const isCoverageMeasureComplete = days >= 1 && !filtersApplied;

  const heatmapIndicatorState = getHeatmapIndicatorState({
    days,
    coverageLevel,
    filtersApplied,
    isError,
  });

  const onDismiss = () => {
    tippyInstance.close();
  };

  const onUpsellClick = () => {
    if (site) {
      upsell({
        site,
        referrer: 'heatmaps.traffic-indicator',
        additionalTrackingData: {
          shown_traffic_coverage: coveragePercentage.toString(),
          indicator_state: heatmapIndicatorState,
        },
      }).then(noop, noop);
    }
  };

  const trackIndicatorClick = (elementName: string) => {
    if (site) {
      click(elementName, UI_POSITION, getGroupDataForSite(site));
    }
  };

  const trackDropDownView = (elementName: string) => {
    if (site) {
      viewElement(elementName, UI_POSITION, getGroupDataForSite(site));
    }
  };

  const showFooter = isCoverageMeasureComplete && coverageLevel !== TrafficCoverageLevel.Max;

  if (isLoading || !site) {
    return null;
  }
  return (
    <S.StyledDropDown
      placement="bottom-end"
      trigger={
        <HeatmapsIndicatorTrafficStatus
          coveragePercentage={coveragePercentage}
          isCoverageMeasureComplete={isCoverageMeasureComplete}
          coverageLevel={coverageLevel}
          text={t(heatmapIndicatorState)}
          trackIndicatorClick={trackIndicatorClick}
        />
      }
      onCreate={tippyInstance.set}
      onShown={() => trackDropDownView('heatmap.indicator-dropdown')}
    >
      <S.Container>
        <S.ContentWrapper>
          <S.Title textType="subtitle1" as="p">
            {isCoverageMeasureComplete && (
              <S.IconContainer>
                <IndicatorIcon
                  coveragePercentage={coveragePercentage}
                  isCoverageMeasureComplete={isCoverageMeasureComplete}
                  coverageLevel={coverageLevel}
                />
              </S.IconContainer>
            )}
            {t('title', { context: heatmapIndicatorState, coveragePercentage })}
          </S.Title>
          <Text
            textType="body2"
            css={css`
              white-space: pre-line;
            `}
          >
            <Trans
              i18nKey={`common:heatmap-indicator.content-${heatmapIndicatorState}`}
              components={[
                <S.ContentDescription />,
                <Text
                  textType="body1"
                  textColor="text_light"
                  style={{ marginRight: tokens.spacer.x0pt5 }}
                />,
                <Text
                  textType="body1"
                  textColor="text_light"
                  style={{ marginRight: tokens.spacer.x0pt5 }}
                />,
              ]}
              values={{ coveragePercentage }}
            />
          </Text>
        </S.ContentWrapper>
        <CloseAction onClick={onDismiss} description={t('close')} />
      </S.Container>
      {showFooter && (
        <S.Footer>
          <Text textType="caption">{t('footer', { context: heatmapIndicatorState })}</Text>
          <Button icon="crown" onClick={onUpsellClick} size="s">
            {t('upgrade')}
          </Button>
        </S.Footer>
      )}
    </S.StyledDropDown>
  );
};
